<template>
	<div>
		<b-breadcrumb>
			<b-breadcrumb-item to="/">{{$title}}</b-breadcrumb-item>
			<b-breadcrumb-item active>Reports</b-breadcrumb-item>
		</b-breadcrumb>

		<b-overlay :show="!!(user && user.id && !user.is_pro)" variant="dark" opacity="0.75">
			<fieldset :disabled="!(user && user.is_pro)">
				<b-card>
					<h5 class="card-title d-flex align-items-center flex-wrap" style="gap: .5rem">
						<span class="mr-auto">
							Income
							<b-badge v-if="user && !user.is_pro" variant="warning">PRO</b-badge>
						</span>
						<!--
							The rollup is a derived cache, so rebuilding it is safe to offer here: it
							only ever restates what the donation ledger already says. It exists because
							the hourly cron heals a trailing window and never removes a bucket, so
							donations written straight into the ledger — an import, a correction — stay
							invisible to the chart until the whole series is re-derived.
						-->
						<b-button size="sm" variant="outline-light" :disabled="rebuilding" @click="rebuildIncome()" title="Re-derive the chart from the donation ledger">
							<b-spinner v-if="rebuilding" small class="mr-1"></b-spinner>
							<b-icon v-else icon="arrow-repeat" class="mr-1"></b-icon>Rebuild
						</b-button>
						<b-button size="sm" variant="outline-light" :disabled="!hasData || downloading" @click="downloadCsv()">
							<b-spinner v-if="downloading" small class="mr-1"></b-spinner>
							<b-icon v-else icon="download" class="mr-1"></b-icon>CSV
						</b-button>
					</h5>

					<!-- Filters, one row above the chart -->
					<div class="rep-controls d-flex flex-wrap align-items-center mb-2" style="gap: .35rem .6rem">
						<b-button-group size="sm">
							<b-button
								v-for="g in granularities"
								:key="g.id"
								:variant="granularity === g.id ? 'primary' : 'outline-light'"
								@click="setGranularity(g.id)"
							>{{ g.label }}</b-button>
						</b-button-group>

						<span class="rep-sep"></span>

						<b-button-group size="sm">
							<b-button
								v-for="g in groupings"
								:key="g.id"
								:variant="groupBy === g.id ? 'primary' : 'outline-light'"
								@click="setGroupBy(g.id)"
							>{{ g.label }}</b-button>
						</b-button-group>

						<span class="rep-sep"></span>

						<b-button-group size="sm">
							<b-button
								v-for="r in ranges"
								:key="r.label"
								:variant="rangeState === r.days ? 'primary' : 'outline-light'"
								@click="onRangeClick(r.days)"
							>{{ r.label }}</b-button>
						</b-button-group>
						<small v-if="rangeState === 'custom'" class="text-muted">custom zoom</small>

						<span class="rep-sep"></span>

						<b-button size="sm" :variant="showTable ? 'primary' : 'outline-light'" @click="showTable = !showTable">
							<b-icon icon="table"></b-icon> Table
						</b-button>
					</div>

					<!-- Headline total for the visible range -->
					<div v-if="hasData" class="mb-2">
						<div class="rep-hero">{{ formatMoney(totals.total, currency) }}</div>
						<small class="text-muted">{{ totals.donations.toLocaleString() }} donation{{ totals.donations === 1 ? '' : 's' }}</small>
					</div>

					<!--
						Legend doubles as the series filter. The colour dot always sits next to the
						series name: the house palette's adjacent hues are close under deuteranopia,
						so identity is never carried by colour alone here or in the tooltip.
					-->
					<div v-if="seriesList.length > 1" class="d-flex flex-wrap align-items-center mb-1" style="gap: .25rem .5rem">
						<button
							v-for="s in seriesList"
							:key="s.key"
							type="button"
							class="rep-chip"
							:class="{ off: !isVisible(s.key) }"
							:title="isVisible(s.key) ? 'Click to hide' : 'Click to show'"
							@click="toggleSeries(s.key)"
						>
							<span class="rep-dot" :style="{ background: colorFor(s.key) }"></span>{{ s.label }}
						</button>
						<a href="#" class="small text-muted ml-1" @click.prevent="allSeries(true)">all</a>
						<a href="#" class="small text-muted" @click.prevent="allSeries(false)">none</a>
					</div>

					<div class="rep-chart-wrap position-relative">
						<div v-show="!loading && !error && hasData" ref="chart" class="rep-chart"></div>
						<div v-if="loading" class="rep-overlay text-muted"><b-spinner small class="mr-2"></b-spinner>Loading…</div>
						<div v-else-if="error" class="rep-overlay text-danger px-3 text-center">{{ error.error || error.toString() }}</div>
						<!-- Suppressed for non-pro users: the upgrade overlay sits on top of this, so the
							empty-state text bled through from behind the CTA button. -->
						<div v-else-if="!hasData && user.is_pro" class="rep-overlay text-muted px-3 text-center">
							No income recorded yet.<br />Totals are rolled up once an hour, so a donation can take up to an hour to appear.
						</div>
					</div>
					<small v-if="hasData" class="text-muted">Click a bar to narrow the donations below to that period.</small>

					<!-- Table view: the same numbers without relying on colour at all -->
					<div v-if="showTable && hasData" class="mt-3">
						<b-table small striped hover responsive :items="tableItems" :fields="tableFields" class="mb-0"></b-table>
						<small v-if="tableTruncated" class="text-muted">
							Showing the first {{ tableItems.length.toLocaleString() }} of {{ tableRows.length.toLocaleString() }} rows — download the CSV for the full report.
						</small>
					</div>
				</b-card>

				<!-- The ledger: individual donations, always on. Follows the chart's time frame, and
					narrows to a single bucket when a chart point is clicked. -->
				<b-card class="mt-3">
					<h5 class="card-title d-flex align-items-center flex-wrap" style="gap: .5rem">
						<!-- No label until a range has actually been resolved, or this reads "Donations ·". -->
						<span class="mr-auto">Donations <small v-if="ledger.label" class="text-muted">· {{ ledger.label }}</small></span>
						<b-button v-if="ledger.narrowed" size="sm" variant="outline-light" @click="resetLedgerToRange()">
							<b-icon icon="x" class="mr-1"></b-icon>Whole range
						</b-button>
						<b-button size="sm" variant="outline-light" :disabled="!ledger.total || ledger.downloading" @click="downloadLedgerCsv()">
							<b-spinner v-if="ledger.downloading" small class="mr-1"></b-spinner>
							<b-icon v-else icon="download" class="mr-1"></b-icon>CSV
						</b-button>
					</h5>

					<div v-if="ledger.loading" class="text-muted py-3"><b-spinner small class="mr-2"></b-spinner>Loading…</div>
					<b-alert v-else-if="ledger.error" show variant="danger">{{ ledger.error.error || ledger.error.toString() }}</b-alert>
					<div v-else-if="!ledger.total" class="text-muted py-3">
						No individual donations recorded for this period.
						<template v-if="hasData"><br />Only donations received since the ledger was added appear here.</template>
					</div>
					<div v-else>
						<b-table small striped hover responsive :items="ledgerItems" :fields="ledgerFields" class="mb-2">
							<template #cell(channel)="data">
								<b-avatar :src="data.item.service_icon_url" size="1.9em" square :title="data.item.service_name" class="mr-1"></b-avatar>
								<!-- The platform is icon-only on screen, so keep the name for assistive tech. -->
								<span class="sr-only">{{ data.item.service_name }}</span>
								<b-avatar size="1.9em" :src="data.item.channel_avatar_url || null" class="mr-2"></b-avatar>{{ data.item.channel }}
							</template>
							<template #cell(donor)="data">
								<b-avatar size="1.9em" :src="data.item.donor_avatar_url || null" class="mr-2"></b-avatar>{{ data.item.donor }}
							</template>
							<!--
								Spans, not text: what the donor wrote keeps its emotes and formatting,
								rendered exactly the way the chat log renders it.
							-->
							<template #cell(message)="data">
								<span v-if="data.item.spans_html" class="rep-msg" v-html="data.item.spans_html"></span>
								<div v-if="data.item.media_url" class="rep-media">
									<b-icon icon="music-note-beamed" class="mr-1"></b-icon>
									<a :href="data.item.media_url" target="_blank" rel="noopener noreferrer nofollow" :title="data.item.media_url">{{ data.item.media_title || data.item.media_url }}</a>
								</div>
							</template>
							<!--
								The amount as the platform charged it, with what it converted to beside it
								when the two currencies differ. The fee is the platform's cut, quoted in
								the platform's own currency — the one the leading amount is in.
							-->
							<template #cell(amount)="data">
								{{ data.item.amount }}
								<span v-if="data.item.amount_converted" class="rep-converted">({{ data.item.amount_converted }})</span>
								<div v-if="data.item.fee_string" class="rep-fee">fee {{ data.item.fee_string }}</div>
							</template>
						</b-table>
						<pager :per-page="ledger.perPage" :skip="ledger.skip" :total="ledger.total" @change="setLedgerPage"></pager>
					</div>
				</b-card>
			</fieldset>

			<template #overlay>
				<div class="text-center p-4">
					<p class="mb-3">Track your donation income over time, broken down by channel and platform.<br />Available for PRO users only.</p>
					<b-button variant="warning" @click="$root.$emit('show-upgrade-modal')">Upgrade to PRO</b-button>
				</div>
			</template>
		</b-overlay>
	</div>
</template>

<script>
import uPlot from 'uplot';
import 'uplot/dist/uPlot.min.css';
import Pager from '@/components/Pager.vue';
import util from '@/util';

// Shared with the Statistics panel so a channel keeps the same colour across the dashboard.
// Assigned in fixed order and keyed to the series' position in the unfiltered list, so hiding a
// series never repaints the others.
const PALETTE = ['#4e9cff', '#ff6b6b', '#39d98a', '#ffb020', '#b980ff', '#00c2d1', '#f25f9c', '#8bc34a', '#ff8f4d', '#7986cb'];
const AXIS_COLOR = 'rgba(200,200,220,0.75)';
const GRID_COLOR = 'rgba(160,160,190,0.14)';

const DAY = 86400;
// Cap on rows rendered in the table view; anything beyond it is reported, never silently dropped.
const TABLE_LIMIT = 500;

// Compact axis tick labels: 1000 -> "1k", 1500 -> "1.5k", 2000000 -> "2M".
function formatAxisNumber(v) {
	const abs = Math.abs(v);
	if(abs >= 1e6) return (Math.round(v / 1e5) / 10) + 'M';
	if(abs >= 1e3) return (Math.round(v / 1e2) / 10) + 'k';
	return String(Math.round(v * 100) / 100);
}

export default {
	name: 'Reports',
	components: {
		Pager,
	},
	data() { return {
		// The ledger panel is always on: it follows the chart's time frame, and narrows to a single
		// bucket when a chart point is clicked.
		ledger: {
			loading: true, downloading: false, error: null,
			results: [], total: 0, skip: 0, perPage: 20,
			from: null, to: null, label: '', narrowed: false,
		},
		user: {},
		loading: true,
		downloading: false,
		rebuilding: false,
		error: null,
		granularity: 'day',
		groupBy: 'total',
		rangeState: 30,
		showTable: false,
		selected: {},
		report: null,
		granularities: [
			{ id: 'hour', label: 'Hourly' },
			{ id: 'day', label: 'Daily' },
			{ id: 'week', label: 'Weekly' },
			{ id: 'month', label: 'Monthly' },
			{ id: 'year', label: 'Yearly' },
		],
		groupings: [
			{ id: 'total', label: 'Total' },
			{ id: 'service', label: 'Per platform' },
			{ id: 'channel', label: 'Per channel' },
		],
		ranges: [
			{ label: '7d', days: 7, phrase: 'last 7 days' },
			{ label: '30d', days: 30, phrase: 'last 30 days' },
			{ label: '90d', days: 90, phrase: 'last 90 days' },
			{ label: '1y', days: 365, phrase: 'last year' },
			{ label: 'All', days: null, phrase: 'all time' },
		],
	}},
	computed: {
		// The server splits every bucket by the currency it was earned in, because a user who changed
		// their channel currency mid-history has rows in both. The report doesn't carry that split:
		// everything is shown in the user's current currency, so those rows are folded back together
		// here — once, so that the series, the totals, the chart and the table all agree.
		rows() {
			const merged = new Map();
			for(const r of this.report?.series || []) {
				const key = `${r.t}|${this.seriesKey(r)}`;
				const row = merged.get(key);
				if(row) {
					row.total += r.total;
					row.donation_count += r.donation_count;
				} else {
					merged.set(key, { ...r, currency: undefined });
				}
			}
			return Array.from(merged.values());
		},
		hasData() { return this.rows.length > 0; },
		timezone() { return this.report?.timezone || 'UTC'; },
		// Everything on this page is money in one currency: the user's own.
		currency() { return this.report?.currency || 'USD'; },
		// Human name for the chart's current time frame, used as the ledger's default heading.
		rangeLabel() {
			if(this.rangeState === 'custom') return 'selected range';
			const r = this.ranges.find(x => x.days === this.rangeState);
			return r ? r.phrase : 'selected range';
		},

		// One series per group.
		seriesList() {
			const seen = new Map();
			for(const r of this.rows) {
				const key = this.seriesKey(r);
				if(seen.has(key)) continue;
				seen.set(key, { key, label: this.seriesLabel(r) });
			}
			return Array.from(seen.values());
		},
		// The headline figure, across whatever the legend currently leaves visible.
		totals() {
			let total = 0, donations = 0;
			for(const r of this.rows) {
				if(!this.isVisible(this.seriesKey(r))) continue;
				total += r.total;
				donations += r.donation_count;
			}
			return { total, donations };
		},
		// t -> end-of-bucket, so a click knows the exact period to ask the ledger for. Every series
		// shares a bucket's bounds, so last-writer-wins is fine here.
		bucketEnds() { return new Map(this.rows.map(r => [r.t, r.t_end])); },

		ledgerFields() {
			// The platform rides in the Channel cell as an icon ahead of the channel avatar; its
			// name stays in the cell for screen readers, and the CSV keeps it as a text column.
			const fields = [
				{ key: 'when', label: 'When' },
				{ key: 'channel', label: 'Channel' },
				{ key: 'donor', label: 'Donor' },
				{ key: 'gift', label: 'Type' },
			];
			// Most platforms send neither a message nor a track with a donation, and a column of
			// empty cells is worse than no column — so it only appears once a page actually has one.
			if(this.ledgerHasMessages) fields.push({ key: 'message', label: 'Message' });
			fields.push({ key: 'amount', label: 'Amount', class: 'text-right' });
			return fields;
		},
		ledgerHasMessages() {
			return this.ledger.results.some(d => d.spans?.length || d.media_url);
		},
		ledgerItems() {
			// The raw row is spread through so the cell slots can reach the avatars; the string
			// fields below are what b-table sorts and filters on.
			return this.ledger.results.map(d => {
				// Lead with the donation as the platform charged it — the same currency the fee is
				// quoted in — and fall back to the converted figure when the platform named no
				// currency at all.
				const charged = d.currency && d.value > 0;
				// The conversion only earns a mention when it says something different. A converted
				// total of zero against a real amount means no exchange rate existed when the
				// donation arrived; it is priced by the backfill once one does, and until then
				// printing "($0.00)" would be a lie.
				const priced = d.converted_total > 0;
				const showConverted = charged && priced && d.currency !== d.converted_currency;
				return {
					...d,
					when: new Date(d.sent_at).toLocaleString(undefined, { timeZone: this.timezone, month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }),
					service: d.service_name || d.service_id,
					donor: d.display_name || d.chatter_token || '—',
					channel: d.channel_title || d.channel_token,
					// Show the per-unit price and count when a gift was sent in bulk, since the amount
					// column is the product and would otherwise look wrong next to the platform's UI.
					gift: `${d.gift_type || '—'}${d.multiplier > 1 ? ` ×${d.multiplier}` : ''}`,
					amount: charged
						? this.formatMoney(d.value * d.multiplier, d.currency)
						: this.formatMoney(d.converted_total, d.converted_currency),
					amount_converted: showConverted ? this.formatMoney(d.converted_total, d.converted_currency) : null,
					// Quoted in the platform's currency (the one `value` is in), not the user's — no
					// conversion is stored for it, and converting it here would be an invention.
					fee_string: d.fee == null ? null : this.formatMoney(d.fee, d.currency || d.converted_currency),
					spans_html: d.spans?.length ? util.spansToHtml(d.spans) : null,
				};
			});
		},
		tableFields() {
			const fields = [{ key: 'bucket', label: 'Period' }];
			if(this.groupBy !== 'total') fields.push({ key: 'series', label: this.groupBy === 'service' ? 'Platform' : 'Channel' });
			fields.push({ key: 'amount', label: 'Income', class: 'text-right' });
			fields.push({ key: 'donations', label: 'Donations', class: 'text-right' });
			return fields;
		},
		// The table is a view of what the chart is showing, so it honours the legend filter too.
		// (The CSV export is deliberately not filtered — it returns the whole queried report.)
		tableRows() { return this.rows.filter(r => this.isVisible(this.seriesKey(r))); },
		tableTruncated() { return this.tableRows.length > TABLE_LIMIT; },
		tableItems() {
			// An hourly all-time report is thousands of buckets; rendering every one as a DOM row
			// stalls the page, so the table is capped and says so. The CSV has the full set.
			return this.tableRows.slice(0, TABLE_LIMIT).map(r => ({
				// The CSV keeps the raw '2026-08-01 00:00:00' form for spreadsheets; on screen the
				// bucket reads better formatted to the granularity actually being shown.
				bucket: this.formatBucket(r.t),
				series: this.seriesLabel(r),
				amount: this.formatMoney(r.total, this.currency),
				donations: r.donation_count.toLocaleString(),
			}));
		},
	},
	watch: {
		// Always after the DOM patch: these fire while the old tree is still mounted, and a chart
		// built against a node Vue is about to replace is thrown away with it.
		rows() { this.$nextTick(() => this.render()); },
		selected: {
			deep: true,
			handler() {
				this.$nextTick(() => this.render());
				// The ledger inherits a single-visible-series as a filter, so it follows suit.
				this.loadLedger();
			},
		},
	},
	async mounted() {
		// Sample arrays are rebuilt wholesale on each load, so they stay off the reactive system.
		this.uplot = null;
		this.sig = null;
		this._programmaticScale = false;
		// Monotonic token so a slow ledger response can't overwrite a newer one.
		this._ledgerRequest = 0;
		this._resizeObserver = new ResizeObserver(() => this.resize());

		try {
			this.user = await this.$api.get('userData');
		} catch(e) {
			this.error = e;
		}
		if(this.user?.is_pro) await this.load();
		else { this.loading = false; this.ledger.loading = false; }
	},
	beforeDestroy() {
		this._resizeObserver?.disconnect();
		if(this.uplot) { this.uplot.destroy(); this.uplot = null; }
	},
	methods: {
		seriesKey(row) {
			if(this.groupBy === 'total') return 't';
			if(this.groupBy === 'service') return `s|${row.service_id}`;
			return `c|${row.service_id}|${row.channel_token}`;
		},
		seriesLabel(row) {
			if(this.groupBy === 'total') return 'Total';
			if(this.groupBy === 'service') return row.service_name || `Service ${row.service_id}`;
			return row.channel_title || row.channel_token;
		},
		colorFor(key) {
			const idx = this.seriesList.findIndex(s => s.key === key);
			return PALETTE[(idx < 0 ? 0 : idx) % PALETTE.length];
		},
		// A series is visible until it is explicitly switched off, so an untouched `selected` map
		// means "show everything" — the chart, the legend and the totals all have to agree on that.
		isVisible(key) { return this.selected[key] !== false; },
		toggleSeries(key) { this.$set(this.selected, key, !this.isVisible(key)); },
		allSeries(on) { for(const s of this.seriesList) this.$set(this.selected, s.key, !!on); },

		setGranularity(g) {
			if(this.granularity === g) return;
			this.granularity = g;
			this.load();
		},
		setGroupBy(g) {
			if(this.groupBy === g) return;
			this.groupBy = g;
			this.selected = {};
			this.load();
		},

		queryParams() {
			const params = { granularity: this.granularity, groupBy: this.groupBy };
			if(this.rangeState !== 'custom' && this.rangeState != null) {
				params.from = Math.floor(Date.now() / 1000) - this.rangeState * DAY;
			}
			return params;
		},

		async load() {
			this.loading = true;
			this.error = null;

			try {
				this.report = await this.$api.get('reports/income', this.queryParams());
			} catch(e) {
				this.error = e;
				this.report = null;
			} finally {
				this.loading = false;
			}
			this.$nextTick(() => this.render());
			// A new granularity or range redraws different buckets, so any bucket the ledger was
			// narrowed to no longer exists — follow the chart's time frame again.
			this.resetLedgerToRange();
		},

		// Re-derive the hourly rollup from the ledger, then show the result. Reloading afterwards is
		// the point of the button: the numbers on screen are the stale ones until it does.
		async rebuildIncome() {
			this.rebuilding = true;
			try {
				const res = await this.$api.post('reports/income/rebuild');
				await this.load();
				// `priced` is donations that arrived in a currency we had no exchange rate for and
				// have just been converted — worth calling out, since they appear out of nowhere.
				const priced = res.priced
					? ` ${res.priced.toLocaleString()} newly converted.`
					: '';
				this.$bvToast.toast(
					`${(res.donations || 0).toLocaleString()} donation${res.donations === 1 ? '' : 's'} across ${(res.buckets || 0).toLocaleString()} hour${res.buckets === 1 ? '' : 's'}.${priced}`,
					{ title: 'Report rebuilt', variant: 'success' },
				);
			} catch(e) {
				this.$bvToast.toast(e.error || e.toString(), { title: 'Could not rebuild the report', variant: 'danger' });
			} finally {
				this.rebuilding = false;
			}
		},

		async downloadCsv() {
			this.downloading = true;
			try {
				const csv = await this.$api.downloadText('reports/income', { ...this.queryParams(), format: 'csv' });
				const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
				const a = document.createElement('a');
				a.href = url;
				a.download = `income-${this.granularity}-${this.groupBy}.csv`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			} catch(e) {
				this.$bvToast.toast(e.error || e.toString(), { title: 'Could not download report', variant: 'danger' });
			} finally {
				this.downloading = false;
			}
		},

		formatMoney(value, currency) {
			const n = parseFloat(value) || 0;
			try {
				return new Intl.NumberFormat(undefined, { style: 'currency', currency: currency || 'USD' }).format(n);
			} catch(e) {
				// Crypto and platform currencies (bits, coins…) aren't ISO 4217.
				return `${n.toLocaleString(undefined, { maximumFractionDigits: 4 })} ${currency || ''}`.trim();
			}
		},

		// --- ledger ---

		// The single visible series, when the user has narrowed the legend to one. That is an
		// explicit gesture, so the ledger inherits it as a filter; otherwise it lists every channel
		// and lets the table's columns carry the detail.
		ledgerFilters() {
			if(this.groupBy === 'total') return {};
			const visible = this.seriesList.filter(s => this.isVisible(s.key));
			if(visible.length !== 1) return {};
			const row = this.rows.find(r => this.seriesKey(r) === visible[0].key);
			if(!row) return {};
			return this.groupBy === 'service'
				? { serviceId: row.service_id }
				: { serviceId: row.service_id, channelToken: row.channel_token };
		},

		// Back to the chart's full time frame.
		resetLedgerToRange() {
			const params = this.queryParams();
			this.ledger.from = params.from || null;
			this.ledger.to = null;
			this.ledger.label = this.rangeLabel;
			this.ledger.narrowed = false;
			this.ledger.skip = 0;
			this.loadLedger();
		},
		// Narrow to the single bucket the user clicked on the chart.
		openDrilldown(t) {
			const end = this.bucketEnds.get(t);
			if(!end) return;
			this.ledger.from = t;
			this.ledger.to = end;
			this.ledger.label = this.formatBucket(t);
			this.ledger.narrowed = true;
			this.ledger.skip = 0;
			this.loadLedger();
		},
		setLedgerPage({ skip, perPage }) {
			this.ledger.skip = skip;
			this.ledger.perPage = perPage;
			this.loadLedger();
		},
		ledgerQuery() {
			const q = { ...this.ledgerFilters() };
			if(this.ledger.from) q.from = this.ledger.from;
			if(this.ledger.to) q.to = this.ledger.to;
			return q;
		},
		async loadLedger() {
			const token = ++this._ledgerRequest;
			this.ledger.loading = true;
			this.ledger.error = null;
			try {
				const res = await this.$api.get('reports/donations', { ...this.ledgerQuery(), skip: this.ledger.skip, limit: this.ledger.perPage });
				// Ignore a response a newer request has already superseded.
				if(token !== this._ledgerRequest) return;
				this.ledger.results = res.results || [];
				this.ledger.total = res.total || 0;
			} catch(e) {
				if(token !== this._ledgerRequest) return;
				this.ledger.error = e;
				this.ledger.results = [];
				this.ledger.total = 0;
			} finally {
				if(token === this._ledgerRequest) this.ledger.loading = false;
			}
		},
		async downloadLedgerCsv() {
			this.ledger.downloading = true;
			try {
				const csv = await this.$api.downloadText('reports/donations', { ...this.ledgerQuery(), format: 'csv' });
				const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
				const a = document.createElement('a');
				a.href = url;
				a.download = `donations-${this.ledger.from ? new Date(this.ledger.from * 1000).toISOString().slice(0, 10) : 'all'}.csv`;
				document.body.appendChild(a);
				a.click();
				document.body.removeChild(a);
				URL.revokeObjectURL(url);
			} catch(e) {
				this.$bvToast.toast(e.error || e.toString(), { title: 'Could not download donations', variant: 'danger' });
			} finally {
				this.ledger.downloading = false;
			}
		},

		// --- chart ---
		render() {
			if(!this.hasData) {
				if(this.uplot) { this.uplot.destroy(); this.uplot = null; this.sig = null; }
				return;
			}
			const visible = this.seriesList.filter(s => this.isVisible(s.key));
			const xs = Array.from(new Set(this.rows.map(r => r.t))).sort((a, b) => a - b);
			const xIndex = new Map(xs.map((t, i) => [t, i]));

			const data = [xs];
			for(const s of visible) {
				// A bucket with no row for this series earned nothing, so it is a real zero rather
				// than a gap — a missing donation is an observation, not missing data.
				const col = new Array(xs.length).fill(0);
				for(const r of this.rows) {
					if(this.seriesKey(r) !== s.key) continue;
					col[xIndex.get(r.t)] = r.total;
				}
				data.push(col);
			}

			// Every visible series shares one bars builder: it lays them out side by side within each
			// bucket, so it has to know how many there are.
			const bars = this.createBarsPaths(visible.length);
			const series = [{}, ...visible.map(s => ({
				label: s.label,
				stroke: this.colorFor(s.key),
				fill: this.colorFor(s.key),
				// A hairline of the same colour: any wider and it eats a thin bar from both sides.
				width: 1,
				paths: bars,
				points: { show: false },
				scale: 'y',
			}))];

			const sig = visible.map(s => `${s.label}|${this.colorFor(s.key)}`).join('¦');
			// uPlot's canvas is invisible to Vue, so a re-render that replaces the container silently
			// discards it while this.uplot still looks alive. Detect that and rebuild.
			const detached = this.uplot && this.uplot.root.parentNode !== this.$refs.chart;
			if(!this.uplot || detached || sig !== this.sig) {
				this.buildChart(series, data);
				this.sig = sig;
			} else {
				this.uplot.setData(data);
			}
			this.applyRangeState();
		},

		// Grouped bars: the series of a bucket sit side by side inside it, never stacked — the legend
		// hides series individually, and a stack that silently reflows every time one is switched off
		// makes two bars impossible to compare.
		//
		// uPlot's own bar layout centres one bar per x value, so the group is built by hand through
		// `disp`: x0 is each bar's left edge and size its width, both in x-scale units (seconds),
		// which is what makes this work on a time axis with uneven gaps. Both are read at draw time,
		// so they follow zooming.
		createBarsPaths(seriesCount) {
			const count = Math.max(1, seriesCount);

			// How much of the x axis one bucket's group of bars may occupy. The bucket step is the
			// smallest gap between two buckets rather than the nominal granularity, because a sparse
			// series skips empty periods and the bars still have to fit the tightest pair.
			const groupSpan = u => {
				const xs = u.data[0];
				let step = Infinity;
				for(let i = 1; i < xs.length; i++) {
					const delta = xs[i] - xs[i - 1];
					if(delta > 0 && delta < step) step = delta;
				}
				const range = (u.scales.x.max || 0) - (u.scales.x.min || 0);
				// One bucket on screen: nothing to measure, so fall back to the visible range.
				if(!isFinite(step)) step = range > 0 ? range : 3600;
				// ...and with a handful of buckets, keep them bars rather than slabs.
				if(range > 0) step = Math.min(step, range * 0.08);
				// The rest is the gap that separates one bucket from the next.
				return step * 0.82;
			};

			return uPlot.paths.bars({
				disp: {
					// seriesIdx is 1-based (0 is x), so the first visible series sits at the group's
					// left edge and each subsequent one a bar-width further in.
					x0: { values: (u, seriesIdx) => {
						const span = groupSpan(u);
						const barWidth = span / count;
						const offset = span / 2 - (seriesIdx - 1) * barWidth;
						return u.data[0].map(x => x - offset);
					} },
					size: { values: u => [groupSpan(u) / count] },
				},
			});
		},

		buildChart(series, data) {
			const el = this.$refs.chart;
			if(!el) return;
			if(this.uplot) { this.uplot.destroy(); this.uplot = null; this._resizeObserver.disconnect(); }
			const tz = this.timezone;
			const opts = {
				width: el.clientWidth || 600,
				height: el.clientHeight || 300,
				series,
				// Buckets are computed in the user's timezone server-side, so the axis has to label
				// them in that timezone too or the boundaries look off by the browser's offset.
				tzDate: ts => uPlot.tzDate(new Date(ts * 1000), tz),
				// One measure, one axis. Money is the only thing plotted here.
				scales: { x: { time: true }, y: { range: (u, min, max) => [Math.min(0, min || 0), max == null ? 1 : max] } },
				axes: [
					{ stroke: AXIS_COLOR, grid: { stroke: GRID_COLOR }, ticks: { stroke: GRID_COLOR } },
					{ scale: 'y', stroke: AXIS_COLOR, grid: { stroke: GRID_COLOR }, ticks: { stroke: GRID_COLOR }, values: (u, splits) => splits.map(formatAxisNumber) },
				],
				legend: { show: false },
				cursor: { drag: { x: true, y: false }, focus: { prox: 20 } },
				plugins: [this.createTooltipPlugin()],
				hooks: {
					setScale: [(u, key) => { if(key === 'x') this._syncRange(u); }],
					// uPlot has no click event, so listen on its overlay and use the cursor's index.
					// A drag-zoom ends in a click too, hence the "did the cursor move" guard.
					ready: [u => {
						u.over.style.cursor = 'pointer';
						u.over.addEventListener('mousedown', e => { this._pressAt = [e.clientX, e.clientY]; });
						u.over.addEventListener('click', e => {
							const moved = this._pressAt && (Math.abs(e.clientX - this._pressAt[0]) > 3 || Math.abs(e.clientY - this._pressAt[1]) > 3);
							if(moved || u.cursor.idx == null) return;
							this.openDrilldown(u.data[0][u.cursor.idx]);
						});
					}],
				},
			};
			this.uplot = new uPlot(opts, data, el);
			this._resizeObserver.observe(el);
		},

		createTooltipPlugin() {
			const self = this;
			let tip;
			return { hooks: {
				init(u) {
					tip = document.createElement('div');
					tip.className = 'rep-tip';
					tip.style.display = 'none';
					u.over.appendChild(tip);
					u.over.addEventListener('mouseleave', () => { tip.style.display = 'none'; });
				},
				setCursor(u) {
					const { idx, left, top } = u.cursor;
					if(idx == null || left < 0) { tip.style.display = 'none'; return; }
					let rows = '';
					for(let i = 1; i < u.series.length; i++) {
						const s = u.series[i];
						if(s.show === false) continue;
						const v = u.data[i][idx];
						if(v == null) continue;
						rows += `<div class="r"><span class="d" style="background:${s.stroke}"></span><span class="l">${self.escapeHtml(s.label)}</span><b>${self.escapeHtml(self.formatMoney(v, self.currency))}</b></div>`;
					}
					if(!rows) { tip.style.display = 'none'; return; }
					tip.innerHTML = `<div class="tt">${self.escapeHtml(self.formatBucket(u.data[0][idx]))}</div>${rows}`;
					tip.style.display = 'block';
					const w = u.over.clientWidth, tw = tip.offsetWidth;
					let lx = left + 14;
					if(lx + tw > w) lx = left - tw - 14;
					if(lx < 0) lx = 2;
					tip.style.transform = `translate(${lx}px, ${Math.max(2, top - 10)}px)`;
				},
			} };
		},
		escapeHtml(s) { return String(s).replace(/[&<>]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c])); },
		formatBucket(sec) {
			const opts = { timeZone: this.timezone, year: 'numeric', month: 'short', day: 'numeric' };
			if(this.granularity === 'hour') { opts.hour = '2-digit'; opts.minute = '2-digit'; }
			if(this.granularity === 'month') { delete opts.day; }
			if(this.granularity === 'year') { delete opts.day; delete opts.month; }
			try {
				return new Date(sec * 1000).toLocaleString(undefined, opts);
			} catch(e) {
				return new Date(sec * 1000).toISOString();
			}
		},

		// --- zoom / range ---
		onRangeClick(days) {
			this.rangeState = days;
			// Presets narrower than what we already hold are pure zoom; wider ones need a refetch.
			this.load();
		},
		applyRangeState() {
			if(!this.uplot || this.rangeState === 'custom') return;
			const xs = this.uplot.data[0];
			if(!xs || !xs.length) return;
			this._programmaticScale = true;
			this.uplot.setScale('x', { min: xs[0], max: xs[xs.length - 1] });
		},
		_syncRange(u) {
			if(this._programmaticScale) { this._programmaticScale = false; return; }
			const xs = u.data[0];
			if(!xs || !xs.length) return;
			const dataMin = xs[0], dataMax = xs[xs.length - 1];
			const { min, max } = u.scales.x;
			const tol = Math.max(2, (dataMax - dataMin) * 0.02);
			this.rangeState = (min <= dataMin + tol && max >= dataMax - tol) ? this.rangeState : 'custom';
		},
		resize() {
			const el = this.$refs.chart;
			if(this.uplot && el && el.clientWidth) this.uplot.setSize({ width: el.clientWidth, height: el.clientHeight });
		},
	},
};
</script>

<style scoped>
/*
	Inactive filter buttons. Bootstrap's outline-secondary (#6c757d on a dark panel) is close to
	unreadable here, so these are outline-light with the border toned back — the label keeps a bright,
	legible ink while five stacked white borders don't shout over the chart.
*/
.rep-controls >>> .btn-outline-light {
	color: rgba(238, 238, 248, 0.92);
	border-color: rgba(180, 180, 210, 0.45);
}
.rep-controls >>> .btn-outline-light:hover,
.rep-controls >>> .btn-outline-light:focus {
	color: #fff;
	background-color: rgba(180, 180, 210, 0.22);
	border-color: rgba(200, 200, 225, 0.75);
}
/* A disabled group (non-pro, behind the upgrade overlay) should still read as deliberately inert. */
.rep-controls >>> .btn-outline-light:disabled {
	color: rgba(238, 238, 248, 0.55);
	border-color: rgba(180, 180, 210, 0.3);
}

.rep-sep {
	width: 1px;
	align-self: stretch;
	background: rgba(160, 160, 190, 0.3);
	margin: 2px 0;
}
.rep-hero {
	font-size: 1.5rem;
	font-weight: 600;
	line-height: 1.1;
}
.rep-chip {
	display: inline-flex;
	align-items: center;
	white-space: nowrap;
	background: rgba(160, 160, 190, 0.12);
	border: 1px solid rgba(160, 160, 190, 0.25);
	border-radius: 10px;
	color: inherit;
	font-size: .8rem;
	line-height: 1.2;
	padding: 1px 8px 1px 6px;
	cursor: pointer;
}
.rep-chip:hover { background: rgba(160, 160, 190, 0.22); }
.rep-chip.off { opacity: .4; }
.rep-chip.off .rep-dot { filter: grayscale(1); }
.rep-dot {
	display: inline-block;
	width: 10px;
	height: 10px;
	border-radius: 50%;
	margin-right: .3rem;
	flex: 0 0 auto;
}
/* The conversion is context for the amount beside it, not a second figure competing with it. */
.rep-converted {
	color: rgba(238, 238, 248, 0.6);
	white-space: nowrap;
}
/* Secondary to the amount it hangs under: smaller and dimmer, but still legible on the dark panel. */
.rep-fee {
	font-size: .75rem;
	line-height: 1.2;
	color: rgba(238, 238, 248, 0.6);
	white-space: nowrap;
}
/*
	Donation messages are one or two lines in practice, but nothing stops a donor from pasting an
	essay — cap the cell so one row can't own the whole table, and let the rest scroll.
*/
.rep-msg {
	display: inline-block;
	max-width: 32em;
	max-height: 6em;
	overflow-y: auto;
	overflow-wrap: anywhere;
	vertical-align: top;
}
/* Emotes and stickers ride the text line rather than setting the row's height. */
.rep-msg >>> img,
.rep-msg >>> video {
	height: 1.4em;
	width: auto;
	vertical-align: text-bottom;
}
.rep-media {
	font-size: .8rem;
	max-width: 32em;
	overflow-wrap: anywhere;
}
.rep-chart-wrap {
	height: 320px;
	min-height: 200px;
}
.rep-chart {
	position: absolute;
	inset: 0;
}
.rep-overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
}
</style>

<style>
/* uPlot renders its own (unscoped) DOM, so these are global but namespaced to this view. */
.rep-tip {
	position: absolute;
	top: 0;
	left: 0;
	pointer-events: none;
	z-index: 10;
	background: rgba(20, 20, 38, 0.95);
	border: 1px solid rgba(160, 160, 190, 0.3);
	border-radius: 4px;
	padding: 5px 7px;
	font-size: 11px;
	line-height: 1.5;
	color: #fff;
	white-space: nowrap;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
}
.rep-tip .tt { font-weight: 600; margin-bottom: 2px; opacity: .85; }
.rep-tip .r { display: flex; align-items: center; }
.rep-tip .r .d { display: inline-block; width: 9px; height: 9px; border-radius: 50%; margin-right: 5px; flex: 0 0 auto; }
.rep-tip .r .l { margin-right: 8px; }
.rep-tip .r b { margin-left: auto; }
</style>
